/**
 * Military Identification Configuration
 *
 * Callsign patterns, ICAO hex ranges, MMSI patterns, and theater
 * monitoring regions for OSINT military aircraft/vessel identification.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CallsignPattern {
  id: string;
  pattern: RegExp;
  branch: string;
  description: string;
  aircraftTypes?: string[];
}

export interface IcaoHexRange {
  start: string;
  end: string;
  description: string;
}

export interface NavalMMSIPattern {
  country: string;
  midRanges: number[];
  description: string;
}

export interface TheaterRegion {
  id: string;
  name: string;
  bounds: { north: number; south: number; east: number; west: number };
  priority: 1 | 2 | 3 | 4 | 5;
  description: string;
  keyBases: string[];
}

export interface CallsignMatch {
  pattern: CallsignPattern;
  callsign: string;
}

// ---------------------------------------------------------------------------
// 1. US Military Callsign Patterns
// ---------------------------------------------------------------------------

export const US_CALLSIGN_PATTERNS: CallsignPattern[] = [
  // USAF — Airlift / Transport
  { id: 'rch', pattern: /^RCH\d{2,4}$/i, branch: 'USAF', description: 'AMC strategic airlift', aircraftTypes: ['C-17', 'C-5M'] },
  { id: 'reach', pattern: /^REACH\d{2,4}$/i, branch: 'USAF', description: 'AMC airlift (extended)', aircraftTypes: ['C-17', 'C-5M', 'C-130J'] },
  { id: 'gold', pattern: /^GOLD\d{2,4}$/i, branch: 'USAF', description: 'AMC priority airlift', aircraftTypes: ['C-17', 'C-5M'] },
  // USAF — Fighter / Attack
  { id: 'viper', pattern: /^VIPER\d{1,3}$/i, branch: 'USAF', description: 'F-16 flight', aircraftTypes: ['F-16C/D'] },
  { id: 'cobra', pattern: /^COBRA\d{1,3}$/i, branch: 'USAF', description: 'Fighter/attack flight', aircraftTypes: ['F-16', 'A-10'] },
  { id: 'raptor', pattern: /^RAPTOR\d{1,3}$/i, branch: 'USAF', description: 'F-22 flight', aircraftTypes: ['F-22A'] },
  { id: 'eagle', pattern: /^EAGLE\d{1,3}$/i, branch: 'USAF', description: 'F-15 flight', aircraftTypes: ['F-15C/E'] },
  { id: 'hawk', pattern: /^HAWK\d{1,3}$/i, branch: 'USAF', description: 'Fighter flight', aircraftTypes: ['F-15', 'F-16'] },
  { id: 'fury', pattern: /^FURY\d{1,3}$/i, branch: 'USAF', description: 'Fighter/attack flight' },
  { id: 'wrath', pattern: /^WRATH\d{1,3}$/i, branch: 'USAF', description: 'Strike mission flight' },
  { id: 'doom', pattern: /^DOOM\d{1,3}$/i, branch: 'USAF', description: 'Bomber/strike flight', aircraftTypes: ['B-1B', 'B-52H'] },
  { id: 'evil', pattern: /^EVIL\d{1,3}$/i, branch: 'USAF', description: 'Aggressor/training flight' },
  { id: 'skull', pattern: /^SKULL\d{1,3}$/i, branch: 'USAF', description: 'Fighter flight' },
  // USAF — Bomber
  { id: 'bison', pattern: /^BISON\d{1,3}$/i, branch: 'USAF', description: 'Bomber task force', aircraftTypes: ['B-52H'] },
  // USAF — Special Ops / Tactical
  { id: 'blade', pattern: /^BLADE\d{1,3}$/i, branch: 'USAF', description: 'SOF rotary/tiltrotor', aircraftTypes: ['CV-22', 'MH-53'] },
  { id: 'saber', pattern: /^SABER\d{1,3}$/i, branch: 'USAF', description: 'Tactical airlift', aircraftTypes: ['C-130J'] },
  { id: 'shadow', pattern: /^SHADOW\d{1,3}$/i, branch: 'USAF', description: 'Special operations flight' },
  { id: 'night', pattern: /^NIGHT\d{1,3}$/i, branch: 'USAF', description: 'Night operations flight' },
  { id: 'storm', pattern: /^STORM\d{1,3}$/i, branch: 'USAF', description: 'Tactical flight' },
  { id: 'havoc', pattern: /^HAVOC\d{1,3}$/i, branch: 'USAF', description: 'Close air support flight', aircraftTypes: ['A-10C'] },
  { id: 'moose', pattern: /^MOOSE\d{1,3}$/i, branch: 'USAF', description: 'Heavy airlift', aircraftTypes: ['C-17', 'C-5M'] },
  { id: 'lance', pattern: /^LANCE\d{1,3}$/i, branch: 'USAF', description: 'Fighter flight' },
  { id: 'sniper', pattern: /^SNIPER\d{1,3}$/i, branch: 'USAF', description: 'Precision strike flight' },
  { id: 'bravo', pattern: /^BRAVO\d{1,3}$/i, branch: 'USAF', description: 'General tactical flight' },
  { id: 'volt', pattern: /^VOLT\d{1,3}$/i, branch: 'USAF', description: 'Electronic warfare flight', aircraftTypes: ['EC-130H'] },
  // USAF — Rescue / Tanker support
  { id: 'king', pattern: /^KING\d{1,3}$/i, branch: 'USAF', description: 'Combat rescue', aircraftTypes: ['HC-130J', 'HH-60W'] },
  { id: 'teal', pattern: /^TEAL\d{1,3}$/i, branch: 'USAF', description: 'VIP / special airlift' },
  // USAF — ISR / Recon
  { id: 'forte', pattern: /^FORTE\d{1,2}$/i, branch: 'USAF', description: 'RQ-4 Global Hawk ISR', aircraftTypes: ['RQ-4B'] },
  { id: 'jake', pattern: /^JAKE\d{1,3}$/i, branch: 'USAF', description: 'RQ-4 surveillance', aircraftTypes: ['RQ-4B'] },
  { id: 'homer', pattern: /^HOMER\d{1,3}$/i, branch: 'USAF', description: 'RC-135 SIGINT', aircraftTypes: ['RC-135V/W'] },
  { id: 'magma', pattern: /^MAGMA\d{1,3}$/i, branch: 'USAF', description: 'Experimental / ISR flight' },
  { id: 'sigint', pattern: /^SIGINT\d{1,3}$/i, branch: 'USAF', description: 'Signals intelligence', aircraftTypes: ['RC-135'] },
  // Tankers
  { id: 'shell', pattern: /^SHELL\d{1,3}$/i, branch: 'USAF', description: 'Aerial refueling tanker', aircraftTypes: ['KC-135', 'KC-46A'] },
  { id: 'ethyl', pattern: /^ETHYL\d{1,3}$/i, branch: 'USAF', description: 'Aerial refueling tanker', aircraftTypes: ['KC-135R'] },
  { id: 'petro', pattern: /^PETRO\d{1,3}$/i, branch: 'USAF', description: 'Aerial refueling tanker', aircraftTypes: ['KC-135', 'KC-10'] },
  { id: 'exxon', pattern: /^EXXON\d{1,3}$/i, branch: 'USAF', description: 'Aerial refueling tanker', aircraftTypes: ['KC-135R'] },
  { id: 'texac', pattern: /^TEXAC\d{1,3}$/i, branch: 'USAF', description: 'Aerial refueling tanker', aircraftTypes: ['KC-135'] },
  // USN
  { id: 'navy', pattern: /^NAVY\d{1,5}$/i, branch: 'USN', description: 'US Navy flight' },
  { id: 'topgun', pattern: /^TOPGUN\d{1,3}$/i, branch: 'USN', description: 'NAWDC flight', aircraftTypes: ['F/A-18E/F'] },
  { id: 'knight', pattern: /^KNIGHT\d{1,3}$/i, branch: 'USN', description: 'Navy tactical flight' },
  { id: 'salty', pattern: /^SALTY\d{1,3}$/i, branch: 'USN', description: 'Navy patrol', aircraftTypes: ['P-8A'] },
  { id: 'tiger', pattern: /^TIGER\d{1,3}$/i, branch: 'USN', description: 'Navy fighter flight', aircraftTypes: ['F/A-18E/F'] },
  // USMC
  { id: 'marine', pattern: /^MARINE\d{1,5}$/i, branch: 'USMC', description: 'Marine Corps flight' },
  { id: 'condor', pattern: /^CONDOR\d{1,3}$/i, branch: 'USMC', description: 'Marine tactical flight', aircraftTypes: ['MV-22B', 'CH-53K'] },
];

// ---------------------------------------------------------------------------
// 2. NATO / Allied Callsign Patterns
// ---------------------------------------------------------------------------

export const NATO_CALLSIGN_PATTERNS: CallsignPattern[] = [
  // United Kingdom — RAF
  { id: 'ascot', pattern: /^ASCOT\d{2,4}$/i, branch: 'RAF', description: 'RAF strategic transport', aircraftTypes: ['C-17', 'A400M'] },
  { id: 'tartan', pattern: /^TARTAN\d{1,4}$/i, branch: 'RAF', description: 'RAF tactical flight' },
  { id: 'rafair', pattern: /^RRR\d{2,4}$/i, branch: 'RAF', description: 'RAF general flight' },
  // Canada
  { id: 'canforce', pattern: /^CFC\d{2,4}$/i, branch: 'RCAF', description: 'Canadian Armed Forces flight', aircraftTypes: ['CC-177', 'CC-130J'] },
  { id: 'canforce2', pattern: /^CANFORCE\d{1,4}$/i, branch: 'RCAF', description: 'Canadian Forces transport' },
  // Germany — Luftwaffe
  { id: 'gaf', pattern: /^GAF\d{2,4}$/i, branch: 'Luftwaffe', description: 'German Air Force', aircraftTypes: ['A400M', 'Eurofighter'] },
  // France — Armee de l'Air
  { id: 'faf', pattern: /^FAF\d{2,4}$/i, branch: 'AdlA', description: 'French Air & Space Force', aircraftTypes: ['Rafale', 'A400M'] },
  { id: 'ctm', pattern: /^CTM\d{2,4}$/i, branch: 'AdlA', description: 'French military transport (COTAM)' },
  { id: 'cotam', pattern: /^COTAM\d{1,4}$/i, branch: 'AdlA', description: 'French air transport command' },
  // Italy
  { id: 'iaf', pattern: /^IAM\d{2,4}$/i, branch: 'AMI', description: 'Italian Air Force', aircraftTypes: ['Eurofighter', 'C-130J'] },
  // Turkey
  { id: 'tuaf', pattern: /^TUAF\d{2,4}$/i, branch: 'TurAF', description: 'Turkish Air Force', aircraftTypes: ['F-16C/D'] },
  { id: 'taf', pattern: /^THK\d{2,4}$/i, branch: 'TurAF', description: 'Turkish Air Force (THK)' },
  // NATO AWACS / Joint
  { id: 'nato', pattern: /^NATO\d{1,3}$/i, branch: 'NATO', description: 'NATO joint/AWACS', aircraftTypes: ['E-3A'] },
  { id: 'magic', pattern: /^MAGIC\d{1,3}$/i, branch: 'NATO', description: 'NATO AWACS mission' },
  // Australia
  { id: 'raaf', pattern: /^RAAF\d{2,4}$/i, branch: 'RAAF', description: 'Royal Australian Air Force' },
  { id: 'aussie', pattern: /^AUSSIE\d{1,4}$/i, branch: 'RAAF', description: 'RAAF transport flight' },
  // Netherlands
  { id: 'rnlaf', pattern: /^NAF\d{2,4}$/i, branch: 'RNLAF', description: 'Royal Netherlands Air Force' },
  // Poland
  { id: 'plaf', pattern: /^PLF\d{2,4}$/i, branch: 'PLAF', description: 'Polish Air Force' },
  // Norway
  { id: 'norw', pattern: /^NOW\d{2,4}$/i, branch: 'RNoAF', description: 'Royal Norwegian Air Force' },
  // Sweden
  { id: 'swaf', pattern: /^SVF\d{2,4}$/i, branch: 'SwAF', description: 'Swedish Air Force', aircraftTypes: ['Gripen'] },
  // Japan
  { id: 'jasdf', pattern: /^JASDF\d{2,4}$/i, branch: 'JASDF', description: 'Japan Air Self-Defense Force' },
];

const ALL_CALLSIGN_PATTERNS: CallsignPattern[] = [
  ...US_CALLSIGN_PATTERNS,
  ...NATO_CALLSIGN_PATTERNS,
];

// ---------------------------------------------------------------------------
// 3. ICAO Hex Code Ranges (Military)
// ---------------------------------------------------------------------------

export const MILITARY_ICAO_RANGES: Record<string, IcaoHexRange[]> = {
  US: [
    { start: 'AE0000', end: 'AE3FFF', description: 'USAF' },
    { start: 'ADF000', end: 'ADFFFF', description: 'US Army' },
    { start: 'AE4000', end: 'AE7FFF', description: 'US Navy / Marines' },
  ],
  UK: [
    { start: '43C000', end: '43CFFF', description: 'Royal Air Force' },
  ],
  France: [
    { start: '3B0000', end: '3B0FFF', description: 'French military' },
  ],
  Germany: [
    { start: '3F4000', end: '3F4FFF', description: 'German military (Luftwaffe)' },
  ],
  Italy: [
    { start: '300000', end: '3003FF', description: 'Italian military (AMI)' },
  ],
  Australia: [
    { start: '7C8000', end: '7C8FFF', description: 'Royal Australian Air Force' },
  ],
  Canada: [
    { start: 'C0CDF9', end: 'C0CDFF', description: 'Canadian Armed Forces' },
  ],
  Turkey: [
    { start: '4B8000', end: '4B8FFF', description: 'Turkish Air Force' },
  ],
  Israel: [
    { start: '738000', end: '7380FF', description: 'Israeli Air Force' },
  ],
};

// ---------------------------------------------------------------------------
// 4. Naval MMSI Patterns
// ---------------------------------------------------------------------------

export const NAVAL_MMSI_PATTERNS: NavalMMSIPattern[] = [
  { country: 'United States', midRanges: [303, 338, 366, 367, 368, 369], description: 'USN / USCG vessels' },
  { country: 'United Kingdom', midRanges: [232, 233, 234, 235], description: 'Royal Navy' },
  { country: 'France', midRanges: [226, 227, 228], description: 'Marine Nationale' },
  { country: 'Russia', midRanges: [273], description: 'Russian Navy' },
  { country: 'China', midRanges: [412, 413, 414], description: 'PLAN (PLA Navy)' },
  { country: 'Germany', midRanges: [211], description: 'Deutsche Marine' },
  { country: 'Italy', midRanges: [247], description: 'Marina Militare' },
  { country: 'Japan', midRanges: [431], description: 'JMSDF' },
  { country: 'South Korea', midRanges: [440, 441], description: 'ROKN' },
  { country: 'India', midRanges: [419], description: 'Indian Navy' },
  { country: 'Turkey', midRanges: [271], description: 'Turkish Naval Forces' },
  { country: 'Australia', midRanges: [503], description: 'Royal Australian Navy' },
  { country: 'Canada', midRanges: [316], description: 'Royal Canadian Navy' },
  { country: 'Norway', midRanges: [257, 258, 259], description: 'Royal Norwegian Navy' },
  { country: 'Israel', midRanges: [428], description: 'Israeli Navy' },
];

// ---------------------------------------------------------------------------
// 5. Theater Monitoring Regions
// ---------------------------------------------------------------------------

export const THEATER_REGIONS: TheaterRegion[] = [
  {
    id: 'europe',
    name: 'European Theater',
    bounds: { north: 72, south: 35, east: 45, west: -12 },
    priority: 1,
    description: 'Ukraine front, Baltic states, Nordic approaches, NATO eastern flank',
    keyBases: ['Ramstein', 'Lakenheath', 'Mildenhall', 'Grafenwoehr', 'Rzeszow-Jasionka'],
  },
  {
    id: 'middle-east',
    name: 'Middle East / Persian Gulf',
    bounds: { north: 38, south: 12, east: 63, west: 30 },
    priority: 1,
    description: 'Persian Gulf, Strait of Hormuz, Red Sea approaches, Levant',
    keyBases: ['Al Udeid', 'Al Dhafra', 'Incirlik', 'Prince Sultan', 'Camp Arifjan'],
  },
  {
    id: 'indo-pacific',
    name: 'Indo-Pacific / South China Sea',
    bounds: { north: 26, south: -5, east: 125, west: 100 },
    priority: 1,
    description: 'South China Sea, Spratly Islands, Malacca Strait, Philippine Sea',
    keyBases: ['Kadena', 'Anderson', 'Clark', 'Changi', 'Diego Garcia'],
  },
  {
    id: 'korea',
    name: 'Korean Peninsula',
    bounds: { north: 43, south: 33, east: 132, west: 124 },
    priority: 2,
    description: 'DMZ, Sea of Japan, Yellow Sea approaches',
    keyBases: ['Osan', 'Kunsan', 'Pyeongtaek', 'Misawa', 'Yokota'],
  },
  {
    id: 'taiwan-strait',
    name: 'Taiwan Strait',
    bounds: { north: 28, south: 21, east: 123, west: 116 },
    priority: 2,
    description: 'Taiwan Strait, Bashi Channel, first island chain',
    keyBases: ['Kadena', 'Futenma', 'Hualien', 'Zuoying'],
  },
  {
    id: 'arctic',
    name: 'Arctic',
    bounds: { north: 90, south: 66, east: 180, west: -180 },
    priority: 3,
    description: 'Northern Sea Route, GIUK gap, polar approaches',
    keyBases: ['Thule', 'Keflavik', 'Tromso', 'Bodo', 'Eielson'],
  },
  {
    id: 'sahel',
    name: 'Sahel / West Africa',
    bounds: { north: 25, south: 4, east: 24, west: -18 },
    priority: 3,
    description: 'Sahel belt, Lake Chad basin, Gulf of Guinea',
    keyBases: ['Niamey AB 201', 'Agadez AB 101', 'Ouagadougou', 'Accra'],
  },
  {
    id: 'horn-of-africa',
    name: 'Horn of Africa',
    bounds: { north: 18, south: -2, east: 54, west: 32 },
    priority: 3,
    description: 'Bab-el-Mandeb, Gulf of Aden, Somali Basin',
    keyBases: ['Camp Lemonnier', 'Berbera', 'Mombasa'],
  },
  {
    id: 'central-asia',
    name: 'Central Asia',
    bounds: { north: 55, south: 30, east: 80, west: 46 },
    priority: 4,
    description: 'Caspian region, Afghanistan periphery, Fergana Valley',
    keyBases: ['Manas', 'Karshi-Khanabad', 'Bagram (closed)', 'Termez'],
  },
];

// ---------------------------------------------------------------------------
// Lookup Functions
// ---------------------------------------------------------------------------

/**
 * Attempt to match a callsign against known military patterns.
 * Returns the first matching pattern or null.
 */
export function matchMilitaryCallsign(callsign: string): CallsignMatch | null {
  const trimmed = callsign.trim();
  for (const pattern of ALL_CALLSIGN_PATTERNS) {
    if (pattern.pattern.test(trimmed)) {
      return { pattern, callsign: trimmed };
    }
  }
  return null;
}

/**
 * Check whether an ICAO 24-bit hex address falls within known military ranges.
 * Hex should be provided as a 6-character uppercase or lowercase string.
 */
export function isMilitaryHexCode(icaoHex: string): { military: boolean; country?: string; description?: string } {
  const hex = icaoHex.toUpperCase().padStart(6, '0');
  const value = parseInt(hex, 16);
  if (isNaN(value)) return { military: false };

  for (const [country, ranges] of Object.entries(MILITARY_ICAO_RANGES)) {
    for (const range of ranges) {
      const lo = parseInt(range.start, 16);
      const hi = parseInt(range.end, 16);
      if (value >= lo && value <= hi) {
        return { military: true, country, description: range.description };
      }
    }
  }
  return { military: false };
}

/**
 * Determine whether an MMSI belongs to a known naval MID (Maritime Identification Digit) range.
 * The first three digits of a 9-digit MMSI encode the MID.
 */
export function isMilitaryMMSI(mmsi: number): { military: boolean; country?: string; description?: string } {
  if (mmsi < 100000000 || mmsi > 999999999) return { military: false };
  const mid = Math.floor(mmsi / 1000000);

  for (const entry of NAVAL_MMSI_PATTERNS) {
    if (entry.midRanges.includes(mid)) {
      return { military: true, country: entry.country, description: entry.description };
    }
  }
  return { military: false };
}

/**
 * Find which theater monitoring region(s) a coordinate falls within.
 * Returns all matching regions sorted by priority (1 = highest).
 */
export function getTheaterByLocation(lat: number, lng: number): TheaterRegion[] {
  return THEATER_REGIONS
    .filter((t) => {
      const inLat = lat >= t.bounds.south && lat <= t.bounds.north;
      // Handle regions that wrap the antimeridian (e.g. Arctic)
      const inLng =
        t.bounds.west <= t.bounds.east
          ? lng >= t.bounds.west && lng <= t.bounds.east
          : lng >= t.bounds.west || lng <= t.bounds.east;
      return inLat && inLng;
    })
    .sort((a, b) => a.priority - b.priority);
}

/** Return all defined theater regions. */
export function getAllTheaters(): TheaterRegion[] {
  return [...THEATER_REGIONS];
}
